import { AxiosErrorResponse } from '@/@types/Errors';
import { useAuth } from '@/auth/Auth';
import { toast } from '@/hooks/use-toast';
import { convertBytesToMB } from '@/lib/select-options';
import { ProductsService } from '@/services/products.service';
import { normalizeUrlWhenPaste } from '@/utils/normalize';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { UploadCloudIcon } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import CustomSelect from '../shared/CustomSelect/CustomSelect';
import { Input } from '../shared/Input/Input';
import { Alert } from '../ui/alert';
import { Card } from '../ui/card';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import DeliverableCard from './DeliverableCard';
import RenderSkeleton from './edit-product-skeleton';

export default function DeliverableSection({
  t,
  isLoadingProductDetail,
  canUploadDeliverables,
  canEditAccessData,
  id,
  register,
  errors,
  enableAccessInputs,
  files,
  setFiles,
  isUploadingFiles,
  setValue,
  watch,
}) {
  const queryClient = useQueryClient();
  const { authData } = useAuth();
  const [uploadProgress, setUploadProgress] = useState(0);

  const productDelivery = watch('product_delivery');
  const isMemberAreaDelivery = productDelivery === 'external_member_area';

  // Produto que já entrega por área de membros mantém a opção mesmo com a área
  // bloqueada: sem ela o select ficaria em branco e o produtor perderia o valor salvo.
  const canPickMemberArea =
    enableAccessInputs && Boolean(authData?.member_area_enabled);

  const deliveryOptions = [
    { value: 'no_delivery', label: t('common:delivery.external') },
    {
      value: 'attach_files',
      label: t('common:delivery.files'),
      disabled: !enableAccessInputs,
    },
    {
      value: 'external_member_area',
      label: t('common:delivery.member_area'),
      disabled: !canPickMemberArea && !isMemberAreaDelivery,
    },
  ];

  const { data: deliverables, isPending: isLoadingDeliverables } = useQuery({
    queryKey: [`list-deliverables-${id}`],
    queryFn: async () => await ProductsService.listDeliverables(id),
  });

  const { mutate: deleteFile } = useMutation({
    mutationFn: ({ deliverableId }: { deliverableId: string }) =>
      ProductsService.deleteDeliverable({ productId: id, deliverableId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [`list-deliverables-${id}`],
      });
      toast({
        description: t('file_successfully_deleted'),
        variant: 'success',
      });
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('data_submission_error'),
      });
    },
  });

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files ? Array.from(e.target.files) : [];

      const validFiles = selectedFiles.filter((file) => {
        if (file.size > 50 * 1024 * 1024) {
          toast({
            variant: 'error',
            description: t('file_max_50MB'),
          });
          return false;
        }

        if (file.type !== 'application/pdf') {
          toast({
            variant: 'error',
            description: t('only_pdf_files_allowed'),
          });
          return false;
        }

        return true;
      });

      if (validFiles.length > 0) {
        setFiles(validFiles);
      }
    },
    [t]
  );

  useEffect(() => {
    if (isUploadingFiles) {
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 15;
        });
      }, 300);

      return () => clearInterval(interval);
    } else {
      setUploadProgress(0);
    }
  }, [isUploadingFiles]);

  return (
    <section className='gap-6 w-full flex mt-12 flex-col lg:flex-row'>
      <article className='space-y-1 lg:w-[328px]'>
        <h2 className='text-colorText font-medium'>{t('receivable_title')}</h2>
        <p className='text-muted text-sm'>{t('receivable_subtitle')}</p>
      </article>
      <div className='w-full lg:max-w-[680px]'>
        <Card className='w-full p-4 lg:p-6 bg-[09090b]'>
          {isLoadingProductDetail ? (
            <RenderSkeleton count={3} />
          ) : (
            <div className='custom-upload-container'>
              <label
                htmlFor='document'
                className='custom-upload-label flex flex-col'
                data-cy='deliverable-input'
              >
                <UploadCloudIcon
                  width={24}
                  height={24}
                  className='upload-icon text-[#52525B]'
                />
                <span className='upload-text mt-4'>{t('click_to_upload')}</span>
                <span className='upload-text'>{t('pdf_up_to_50MB')}</span>
                <input
                  id='document'
                  type='file'
                  accept='.pdf'
                  className='hidden disabled:cursor-not-allowed'
                  onChange={handleFileChange}
                  disabled={!canUploadDeliverables}
                />
              </label>

              {files.length > 0 && (
                <section className='mt-6'>
                  <Label>{t('loaded_files')}</Label>
                  <div className='mt-2 space-y-2'>
                    {files.map((file, index) => (
                      <DeliverableCard
                        id={id}
                        key={`${file.name}-${index}`}
                        deliverableName={file.name}
                        deliverableSize={convertBytesToMB(file.size)}
                        onDelete={() =>
                          setFiles((prev) => prev.filter((_, i) => i !== index))
                        }
                      />
                    ))}
                  </div>
                </section>
              )}

              {isUploadingFiles && (
                <div className='mt-4'>
                  <div className='flex items-center gap-2 mb-2'>
                    <div className='w-full bg-[#27272A] rounded-full h-2.5'>
                      <div
                        className='bg-primary h-2.5 rounded-full transition-all duration-300'
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className='text-sm text-gray-500'>{uploadProgress}%</p>
                  </div>
                </div>
              )}

              {!isLoadingDeliverables && deliverables?.length > 0 && (
                <div className='mt-4'>
                  <Label>{t('loaded_files_already')}</Label>
                  <div className='mt-2 space-y-2'>
                    {deliverables.map((deliverable) => (
                      <DeliverableCard
                        id={id}
                        key={`uploaded-${deliverable.id}`}
                        deliverableName={deliverable.name}
                        deliverableSize={convertBytesToMB(deliverable.size)}
                        url={deliverable?.url}
                        onDelete={() => {
                          if (canUploadDeliverables) {
                            deleteFile({ deliverableId: deliverable.id });
                          }
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isLoadingProductDetail && (
            <div className='mt-6 pt-6 border-t border-colorBorder space-y-4'>
              <CustomSelect
                id='deliveryMethod'
                dataCy='delivery-method-select'
                label={t('deliveryMethodLabel')}
                placeholder={t('deliveryMethodPlaceholder')}
                options={deliveryOptions}
                value={productDelivery}
                disabled={!canEditAccessData}
                onValueChange={(value) =>
                  setValue('product_delivery', value, { shouldDirty: true })
                }
                helperText={errors?.product_delivery?.message}
              />
              {isMemberAreaDelivery && (
                <Alert variant='info'>
                  <p className='text-sm'>{t('common:delivery.member_area_info')}</p>
                </Alert>
              )}
            </div>
          )}
        </Card>

        <Card
          className={`w-full p-4 lg:p-6 mt-6 bg-[09090b] space-y-4 ${
            isMemberAreaDelivery ? 'hidden' : ''
          }`}
        >
          <Input
            {...register('product_url')}
            label={t('productAccessLink')}
            placeholder={t('productAccessLinkPlaceholder')}
            subLabel={t('productAccessLinkInfo')}
            tooltip={t('productAccessLinkTooltip')}
            helperText={errors?.product_url?.message}
            disabled={!canEditAccessData || !enableAccessInputs}
            data-cy='product-url-input'
            className='mt-6 lg:mt-0'
            onFocus={(e) => {
              if (!e.target.value) {
                e.target.value = 'https://';
                setValue('product_url', 'https://');
              }
            }}
            onPaste={(e) => {
              const finalValue = normalizeUrlWhenPaste(e);
              setValue('product_url', finalValue);
            }}
          />
          <Input
            {...register('product_email')}
            label={t('productEmail')}
            placeholder={t('productEmailPlaceholder')}
            helperText={errors?.product_email?.message}
            disabled={!canEditAccessData || !enableAccessInputs}
            data-cy='product-email-input'
            autoComplete='off'
          />
          <Input
            {...register('product_password')}
            label={t('productPassword')}
            placeholder={t('productPasswordPlaceholder')}
            helperText={errors?.product_password?.message}
            disabled={!canEditAccessData || !enableAccessInputs}
            data-cy='product-password-input'
            type='password'
            autoComplete='new-password'
          />
          <div className='flex flex-col mt-4'>
            <Label htmlFor='product_extras' className='mb-2 block'>
              {t('productExtras')}{' '}
              <span className='text-sm text-muted'>
                ({t('affiliates:optional')})
              </span>
            </Label>
            <Textarea
              {...register('product_extras')}
              placeholder={t('productExtrasPlaceholder')}
              className={`focus:outline-none ring-0 focus-visible:outline-none ${
                errors?.product_extras ? 'border-error' : 'border-colorBorder'
              }`}
              disabled={!canEditAccessData || !enableAccessInputs}
              data-cy='product-extras-textarea'
            />
          </div>
        </Card>
      </div>
    </section>
  );
}
